// src/utils/intelligenceEngine.js
// Shared rule-based Operations Intelligence engine.
// Pure function — takes already-fetched data, returns flags. No Firestore
// calls in here; each caller fetches its own data and passes it in.
// Used by OperationsIntelligence.js (full page) and SupervisorDashboard.jsx
// (the "Needs Your Attention" panel), so both always agree on what counts
// as a critical/high/medium/positive flag.

// `key` maps to a CSS class (sev-critical / sev-high / sev-medium / sev-positive)
// defined in both OperationsIntelligence.css and SupervisorDashboard.css.
export const SEV = {
  CRITICAL: { key: 'critical', label: 'CRITICAL', icon: '🔴', order: 0 },
  HIGH:     { key: 'high',     label: 'HIGH',      icon: '🟠', order: 1 },
  MEDIUM:   { key: 'medium',   label: 'MEDIUM',    icon: '🟡', order: 2 },
  POSITIVE: { key: 'positive', label: 'POSITIVE',  icon: '✅', order: 3 }
};

export function runRules({ services, clockEvents, users }) {
  const flags  = [];
  const now    = new Date();
  const day7   = new Date(now - 7  * 86400000);
  const day30  = new Date(now - 30 * 86400000);

  // Only last 7 days services
  const recent = services.filter(s => {
    const d = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
    return d >= day7;
  });

  const technicians = users.filter(u => u.role === 'technician' && u.active);

  // ── Group services by technician ──────────────────────────────────────────
  const byTech = {};
  technicians.forEach(t => { byTech[t.id] = { tech: t, services: [] }; });
  recent.forEach(s => {
    if (!byTech[s.technicianId]) {
      byTech[s.technicianId] = { tech: { id: s.technicianId, name: s.technicianName }, services: [] };
    }
    byTech[s.technicianId].services.push(s);
  });

  // ── Group services by outlet + equipment ──────────────────────────────────
  const byEquipment = {};
  recent.forEach(s => {
    const key = `${s.outletName}__${s.serialNumber}`;
    if (!byEquipment[key]) byEquipment[key] = { outletName: s.outletName, serialNumber: s.serialNumber, services: [] };
    byEquipment[key].services.push(s);
  });

  // ── Group clock-IN events by technician ─────────────────────────────────
  // Reads the real `clockEvents` collection (userId + type: 'in'/'out' + timestamp).
  const clockByTech = {};
  clockEvents.filter(c => c.type === 'in').forEach(c => {
    if (!clockByTech[c.userId]) clockByTech[c.userId] = [];
    clockByTech[c.userId].push(c);
  });

  // ─────────────────────────────────────────────────────────────────────────
  // RULE 1: Clocked in but zero services (per day, last 7 days)
  // ─────────────────────────────────────────────────────────────────────────
  technicians.forEach(tech => {
    const clocks = (clockByTech[tech.id] || []).filter(c => {
      const d = new Date(c.timestamp);
      return d >= day7;
    });

    // Group clocks by calendar day
    const clockDays = {};
    clocks.forEach(c => {
      const d    = new Date(c.timestamp);
      const key  = d.toDateString();
      clockDays[key] = true;
    });

    // For each clock day, check if any service was recorded
    const zeroDays = Object.keys(clockDays).filter(dayStr => {
      const dayStart = new Date(dayStr);
      const dayEnd   = new Date(dayStart.getTime() + 86400000);
      return !recent.some(s => {
        if (s.technicianId !== tech.id) return false;
        const sd = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
        return sd >= dayStart && sd < dayEnd;
      });
    });

    if (zeroDays.length >= 2) {
      flags.push({
        severity:    SEV.CRITICAL,
        category:    'Attendance vs Output',
        technician:  tech.name,
        title:       `${tech.name} clocked in with zero services recorded`,
        detail:      `${tech.name} clocked in on ${zeroDays.length} day${zeroDays.length > 1 ? 's' : ''} in the last 7 days but recorded NO services. This is a direct business loss — paid time with no output. Investigate immediately.`,
        action:      'Review clock records vs service submissions. Conduct 1-on-1.',
        metric:      `${zeroDays.length} zero-output days`
      });
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // RULE 2: Service records without GPS location
  // ─────────────────────────────────────────────────────────────────────────
  const noGPS = recent.filter(s => !s.location || !s.location.latitude);
  const noGPSByTech = {};
  noGPS.forEach(s => {
    if (!noGPSByTech[s.technicianId]) noGPSByTech[s.technicianId] = { name: s.technicianName, count: 0, orders: [] };
    noGPSByTech[s.technicianId].count++;
    noGPSByTech[s.technicianId].orders.push(`#${s.orderNumber}`);
  });

  Object.values(noGPSByTech).forEach(({ name, count, orders }) => {
    if (count >= 2) {
      flags.push({
        severity:   SEV.CRITICAL,
        category:   'Compliance / Falsification Risk',
        technician: name,
        title:      `${name} submitting records without GPS — ${count} instances`,
        detail:     `${count} service records from ${name} have no GPS location attached (${orders.join(', ')}). This is a red flag for falsified field visits. There is no way to verify these jobs were done at the claimed locations.`,
        action:     'Demand re-submission with GPS or conduct outlet verification visits.',
        metric:     `${count} unverified records`
      });
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // RULE 3: Same equipment breaking down repeatedly (3+ times in 7 days)
  // ─────────────────────────────────────────────────────────────────────────
  Object.values(byEquipment).forEach(({ outletName, serialNumber, services: eqSvcs }) => {
    const emergencies = eqSvcs.filter(s => s.serviceType === 'emergency');
    if (eqSvcs.length >= 3) {
      flags.push({
        severity:   SEV.HIGH,
        category:   'Equipment Reliability',
        technician: null,
        title:      `Equipment at ${outletName} breaking down repeatedly`,
        detail:     `Serial ${serialNumber} at ${outletName} has required ${eqSvcs.length} service visits in 7 days${emergencies.length > 0 ? `, including ${emergencies.length} emergency call${emergencies.length > 1 ? 's' : ''}` : ''}. Repeated breakdowns mean either poor repair quality, end-of-life equipment, or technician not fixing root cause — just patching symptoms.`,
        action:     'Schedule full equipment inspection. Consider replacement if >5 breakdowns in 30 days.',
        metric:     `${eqSvcs.length} visits in 7 days`
      });
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // RULE 4: Emergency spikes at specific outlets (2+ emergencies in 7 days)
  // ─────────────────────────────────────────────────────────────────────────
  const emergencyByOutlet = {};
  recent.filter(s => s.serviceType === 'emergency').forEach(s => {
    if (!emergencyByOutlet[s.outletName]) emergencyByOutlet[s.outletName] = { count: 0, techs: new Set() };
    emergencyByOutlet[s.outletName].count++;
    emergencyByOutlet[s.outletName].techs.add(s.technicianName);
  });

  Object.entries(emergencyByOutlet).forEach(([outlet, { count, techs }]) => {
    if (count >= 2) {
      flags.push({
        severity:   SEV.HIGH,
        category:   'Emergency Patterns',
        technician: null,
        title:      `Emergency spike at ${outlet} — ${count} in 7 days`,
        detail:     `${outlet} has had ${count} emergency callouts in the last 7 days. This outlet is consuming disproportionate emergency resources. Either the equipment is failing, the outlet is misusing the equipment, or technicians are not completing proper fixes.`,
        action:     'Conduct a full audit of this outlet. Review all recent service notes.',
        metric:     `${count} emergencies`
      });
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // RULE 5: Spares usage spike (technician using >2x their own weekly average)
  // ─────────────────────────────────────────────────────────────────────────
  // We approximate spares usage by checking partsUsed field length as proxy
  // (non-empty = parts used)
  Object.values(byTech).forEach(({ tech, services: tSvcs }) => {
    if (tSvcs.length < 2) return;
    const withParts = tSvcs.filter(s => s.partsUsed && s.partsUsed.trim().length > 0);
    const ratio     = withParts.length / tSvcs.length;

    if (ratio > 0.8 && withParts.length >= 4) {
      flags.push({
        severity:   SEV.HIGH,
        category:   'Spares & Cost Control',
        technician: tech.name,
        title:      `${tech.name} using spares on ${Math.round(ratio * 100)}% of jobs`,
        detail:     `${tech.name} recorded spare parts usage on ${withParts.length} out of ${tSvcs.length} service visits this week (${Math.round(ratio * 100)}%). Industry norm is 40-60%. This level of parts usage is abnormally high and could indicate wastage, incorrect diagnosis, or parts being taken without legitimate use.`,
        action:     'Cross-check parts used against spares inventory. Request itemised parts lists.',
        metric:     `${withParts.length}/${tSvcs.length} jobs with parts`
      });
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // RULE 6: Low completion rate (<50% services completed in 7 days)
  // ─────────────────────────────────────────────────────────────────────────
  Object.values(byTech).forEach(({ tech, services: tSvcs }) => {
    if (tSvcs.length < 3) return; // need enough data
    const completed = tSvcs.filter(s => s.status === 'completed').length;
    const rate      = completed / tSvcs.length;

    if (rate < 0.5) {
      flags.push({
        severity:   SEV.HIGH,
        category:   'Performance',
        technician: tech.name,
        title:      `${tech.name} completing only ${Math.round(rate * 100)}% of jobs`,
        detail:     `${tech.name} has completed ${completed} out of ${tSvcs.length} service visits this week — a ${Math.round(rate * 100)}% completion rate. Every incomplete job is an unresolved client issue and a cost with no return. This is not acceptable performance.`,
        action:     'Review reasons for incomplete jobs. Set clear completion targets with consequences.',
        metric:     `${completed}/${tSvcs.length} completed`
      });
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // RULE 7: No photo documentation (>3 services without photos in 7 days)
  // ─────────────────────────────────────────────────────────────────────────
  Object.values(byTech).forEach(({ tech, services: tSvcs }) => {
    if (tSvcs.length < 2) return;
    const noPhoto = tSvcs.filter(s => !s.photoCount || s.photoCount === 0);
    if (noPhoto.length >= 3) {
      flags.push({
        severity:   SEV.MEDIUM,
        category:   'Documentation Compliance',
        technician: tech.name,
        title:      `${tech.name} skipping photo documentation`,
        detail:     `${tech.name} submitted ${noPhoto.length} service records without any photos this week. Photo documentation is mandatory. Without before/after photos there is no evidence of work done, no accountability, and no protection against client disputes.`,
        action:     'Issue formal reminder. Next offence should trigger disciplinary action.',
        metric:     `${noPhoto.length} records without photos`
      });
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // RULE 8: Team avg comparison — underperforming technicians
  // ─────────────────────────────────────────────────────────────────────────
  const techList    = Object.values(byTech).filter(t => t.services.length > 0);
  if (techList.length >= 2) {
    const avg = techList.reduce((sum, t) => sum + t.services.length, 0) / techList.length;
    techList.forEach(({ tech, services: tSvcs }) => {
      if (tSvcs.length < avg * 0.5 && avg >= 4) {
        flags.push({
          severity:   SEV.MEDIUM,
          category:   'Performance',
          technician: tech.name,
          title:      `${tech.name} significantly below team average`,
          detail:     `Team average this week is ${avg.toFixed(1)} services. ${tech.name} recorded only ${tSvcs.length} — ${Math.round((1 - tSvcs.length / avg) * 100)}% below average. Either their territory has fewer jobs (check journey plan) or they are underperforming. Neither is acceptable without explanation.`,
          action:     'Compare journey plan assignments. If territory is equal, address performance directly.',
          metric:     `${tSvcs.length} vs ${avg.toFixed(1)} team avg`
        });
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // POSITIVE flags — things working well
  // ─────────────────────────────────────────────────────────────────────────
  // Top performer
  if (techList.length > 0) {
    const top = techList.sort((a, b) => b.services.length - a.services.length)[0];
    const topCompleted = top.services.filter(s => s.status === 'completed').length;
    if (top.services.length >= 3) {
      flags.push({
        severity:   SEV.POSITIVE,
        category:   'Top Performance',
        technician: top.tech.name,
        title:      `${top.tech.name} is leading the team this week`,
        detail:     `${top.tech.name} recorded ${top.services.length} service visits this week with ${topCompleted} completions. This is the standard every technician should be meeting.`,
        action:     'Acknowledge performance. Use as benchmark for the team.',
        metric:     `${top.services.length} services`
      });
    }
  }

  // 100% photo compliance
  Object.values(byTech).forEach(({ tech, services: tSvcs }) => {
    if (tSvcs.length >= 4 && tSvcs.every(s => s.photoCount > 0)) {
      flags.push({
        severity:   SEV.POSITIVE,
        category:   'Documentation',
        technician: tech.name,
        title:      `${tech.name} — 100% photo compliance this week`,
        detail:     `Every single service record from ${tech.name} this week includes photo documentation. This is exactly the standard required. This technician understands accountability.`,
        action:     'Recognise this publicly. Set as example.',
        metric:     `${tSvcs.length}/${tSvcs.length} with photos`
      });
    }
  });

  // Sort by severity order
  return flags.sort((a, b) => a.severity.order - b.severity.order);
}
